'use client';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  CircularProgress,
  Link as MuiLink,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  fetchTomVendingSiteVisitHistoryRecent,
  type TomVendingSiteVisitHistoryRecentRow,
} from '@/hooks/useTomVendingSiteVisitHistory';
import { formatUsdFromCents } from '@/hooks/useTomVendingReports';
import { useNotification } from '@/contexts/NotificationContext';
import { formatVisitDateLabel } from '@/components/toms-vending/TomsVendingSiteVisitDetailContent';

const PREVIEW_TAKE = 8;

const TomsVendingRecentSiteVisitsSection: React.FC = () => {
  const { showError } = useNotification();
  const [rows, setRows] = useState<TomVendingSiteVisitHistoryRecentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const r = await fetchTomVendingSiteVisitHistoryRecent({ skip: 0, take: PREVIEW_TAKE });
        if (!cancelled) {
          setRows(r.data);
          setTotal(r.total);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : 'Failed to load recent site visits');
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showError]);

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" component="h2">
          Recent site visits
        </Typography>
        {total > PREVIEW_TAKE && (
          <MuiLink
            component={RouterLink}
            to="/app/toms-vending/site-visits/recent"
            variant="body2"
            underline="hover"
          >
            See more
          </MuiLink>
        )}
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No site visit history yet. Record money collected or save machine tallies during a site visit
          to create a report.
        </Typography>
      ) : (
        <>
          {total > rows.length && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Showing {rows.length} of {total} visits across locations.{' '}
              <MuiLink component={RouterLink} to="/app/toms-vending/site-visits/recent" underline="hover">
                See more
              </MuiLink>
            </Typography>
          )}
          <List disablePadding>
            {rows.map((row) => (
              <ListItemButton
                key={row.siteVisitId}
                component={RouterLink}
                to={`/app/toms-vending/locations/${row.locationTomVendingId}?tab=history`}
                sx={{ borderRadius: 1, mb: 0.5, px: 1 }}
              >
                <ListItemText
                  primary={row.locationName || `Location #${row.locationTomVendingId}`}
                  secondary={
                    <>
                      {formatVisitDateLabel(row.visitDate)}
                      {row.userDisplayName ? ` · ${row.userDisplayName}` : ''}
                      {` · ${row.machineSnapshotCount} machine${row.machineSnapshotCount === 1 ? '' : 's'}`}
                      {(row.totalCashCollectedCents ?? 0) > 0
                        ? ` · Cash ${formatUsdFromCents(row.totalCashCollectedCents ?? 0)}`
                        : ''}
                    </>
                  }
                />
                <ChevronRightIcon fontSize="small" color="action" />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default TomsVendingRecentSiteVisitsSection;
